<script lang="ts" setup>
  import { mail } from 'ionicons/icons'
  import { z } from 'zod'
  import { EMAIL_INVALID_ERROR } from '~/constants/errorMessage'
  import { useEmailVerificationModal } from '~/composables/verificationModal'
  import { gaEventVerificationEmailSubmit } from '~/composables/analytics'
  import { updateEmail } from '~/composables/api'
  import type { ViewSource } from '~/types/route'
  import { useNativeHaptics } from '~/composables/nativeHaptics'
  import { useAuthStore, useUserStore } from '~/store'

  const { showEmailVerificationModal, emailVerificationModalId } = useEmailVerificationModal()
  const authStore = useAuthStore()
  const { isWebMode } = useAppMode()
  const userStore = useUserStore()

  const props = defineProps<{
    verificationModalId: string
    source: ViewSource
    initialValue?: string | null
    isVerified?: boolean | null
  }>()

  const emit = defineEmits<{
    (
      event: 'alert',
      alertContent: {
        header: string
        subHeader?: string
        message: string
      }
    ): void
    (event: 'routing'): void
    (event: 'verification-success'): void
  }>()
  const validationSchema = toTypedSchema(
    z.object({
      emailAddress: z
        .string()
        .min(6, { message: 'Minimum 6 characters' })
        .max(100, { message: 'Max 100 characters' })
        .email({ message: EMAIL_INVALID_ERROR })
    })
  )
  const { handleSubmit, errors, isSubmitting, meta, values, resetField } = useForm({
    validationSchema,
    initialValues: {
      emailAddress: props.initialValue ?? ''
    }
  })
  /**
   * Form is valid if:
   * - Form is valid (vee-validate)
   * - No errors (vee-validate)
   */
  const isFormValid = computed(() => {
    return meta.value.valid && !Object.keys(errors.value).length
  })

  const isFormDirty = computed(() => {
    return meta.value.dirty
  })

  const isCtaDisabled = computed<boolean>(() => {
    return !isFormValid.value || (props.isVerified === true && !isFormDirty.value)
  })

  const isCurrentValueVerified = computed<boolean>(() => {
    if (values.emailAddress === props.initialValue) {
      return props.isVerified ?? false
    }
    return false
  })

  watch(
    () => props.initialValue,
    (newValue) => {
      // Reset the field when the initial value changes, to reset the dirty flag
      resetField('emailAddress', { value: newValue ?? '' })
    }
  )

  const onSubmit = handleSubmit(async () => {
    isSubmitting.value = true

    try {
      if (!isFormValid.value) {
        await useNativeHaptics().triggerVibrate()
        emit('alert', {
          header: 'Oops. Unexpected Error.',
          message: 'Please check your connection or try again later.'
        })
        console.warn('Email value is invalid', errors)
        return
      }

      const rawValues = toRaw(values)
      await updateEmail({
        emailAddress: rawValues?.emailAddress ?? ''
      })

      openVerificationModal()

      gaEventVerificationEmailSubmit(props.source)
    } catch (error: any) {
      await useNativeHaptics().triggerVibrate()
      var message = getApiErrorMessage(error) ?? 'Please check your connection or try again later.'
      var title = getApiErrorTitle(error) ?? 'Oops. Unexpected Error.'
      emit('alert', {
        header: title,
        message
      })
      console.warn('Error sending Verification Email', error)
    } finally {
      isSubmitting.value = false
    }
  })

  const openVerificationModal = async () => {
    await showEmailVerificationModal({
      source: props.source,
      title: 'Verify Email',
      message: 'Enter the code we’ve sent to your email',
      showPasswordInput: true,
      onSubmit: onOtpSubmit,
      onSuccessfulVerifyCode: () => onSuccessfulVerifyCode(),
      onSignOut: () => handleSignOut(),
      onDismiss: () => {}
    })
  }

  async function onOtpSubmit(verificationCode: string, password?: string) {
    const accountDetails = await confirmEmail({
      code: verificationCode,
      password: password ?? ''
    })

    gaEventEmailVerificationSuccess(props.source)
    authStore.signIn(accountDetails.session!, accountDetails.user!)
  }

  async function onSuccessfulVerifyCode() {
    await modalController.dismiss(undefined, undefined, emailVerificationModalId)
    emit('verification-success')
  }

  function handleSignOut() {
    if (isWebMode) {
      emitDidSignOutFromWebsite()
    }
    userStore.hasOnboarded = false

    // Dismiss verification modals
    modalController.dismiss(undefined, undefined, emailVerificationModalId)
    modalController.dismiss(undefined, undefined, props.verificationModalId)

    emit('routing')
    authStore.signOut()
  }
</script>

<template>
  <form class="verification-form">
    <div class="verification-container">
      <div class="verification-text" :class="{ verified: isCurrentValueVerified }">
        {{ isCurrentValueVerified ? 'Verified' : 'Unverified' }}
      </div>
    </div>
    <div class="input-with-helper-text">
      <GInputField
        label="Email Address"
        :max-length="100"
        input-mode="email"
        name="emailAddress"
        type="email"
        clear-input
      />
    </div>
    <GButton
      class="button"
      :disabled="isCtaDisabled"
      label="Verify Email"
      :should-show-loading-animation="isSubmitting"
      :icon="mail"
      show-icon-lhs
      animation-color="light"
      block
      variant="primary"
      style="width: 100%"
      @click.prevent.stop="onSubmit"
    />
  </form>
</template>

<style lang="scss" scoped>
  .verification-form {
    background-color: white;
    padding: 16px;
    align-self: stretch;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    border-radius: 16px 16px 0px 0px;
    border-bottom: 1px solid var(--color-secondary-paper-light, #f5f2ef);
    background: white;

    .verification-container {
      display: flex;
      flex-direction: row;
      justify-content: left;
      align-items: flex-start;
      gap: 8px;
      width: 100%;

      .verification-text {
        @include typography-body-small-strong;
        align-self: stretch;
        color: var(--color-base-red);

        &.verified {
          color: var(--color-alert-success);
        }
      }
    }
    .input-with-helper-text {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      width: 100%;
    }

    .button {
      width: 100%;
    }
  }
</style>
